import { createSlice } from "@reduxjs/toolkit";

import { ParsedMarketInformationWithLtvInfo } from "../../types";
import { marketsPublicApi } from "../../reducers";

interface MarketInformationCacheState {
    byId: ParsedMarketInformationWithLtvInfo[];
    loading: boolean;
    isFetching: boolean;
}

const initialState: MarketInformationCacheState = {
    byId: [],
    loading: false,
    isFetching: false
};

interface MarketInformationCacheBranch {
    oracleMarketInformationsCache?: MarketInformationCacheState;
}

export const oracleMarketInformationsCacheSlice = createSlice({
    name: "oracleMarketInformationsCache",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addMatcher(marketsPublicApi.endpoints.oracleMarketInformations.matchPending, (state) => {
                // loading is true only if we have no data yet
                if (Object.keys(state.byId).length === 0) {
                    state.loading = true;
                }
                state.isFetching = true;
            })
            .addMatcher(marketsPublicApi.endpoints.oracleMarketInformations.matchFulfilled, (state, { payload }) => {
                state.loading = false;
                state.isFetching = false;
                // Merge by address: the slim eager prefetch and the scoped full fetches feed the same
                // cache. Upsert each market, but never let a slim response (empty asset info) clobber
                // asset info a full response already loaded — the two can land in either order.
                const byAddress = new Map(state.byId.map((m) => [m.marketInformation.address, m]));
                for (const market of payload) {
                    const existing = byAddress.get(market.marketInformation.address);
                    const marketInformationAssetInfo = market.marketInformationAssetInfo?.length
                        ? market.marketInformationAssetInfo
                        : existing?.marketInformationAssetInfo ?? [];
                    byAddress.set(market.marketInformation.address, { ...market, marketInformationAssetInfo });
                }
                state.byId = Array.from(byAddress.values());
            })
            .addMatcher(marketsPublicApi.endpoints.oracleMarketInformations.matchRejected, (state) => {
                state.loading = false;
                state.isFetching = false;
            });
    }
});

export const selectMarketInformationsCacheState = (state: MarketInformationCacheBranch): MarketInformationCacheState =>
    state.oracleMarketInformationsCache ?? initialState;
